import {
  Alert,
  Descriptions,
  Input,
  Modal,
  message,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { authStore, computerProductionUpgradePolicyStore } from '@/stores';
import type { ComputerProductionUpgradeApproval } from '@/types';

const COMPUTER_PRODUCTION_VERSION =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

function compareVersions(left: string, right: string) {
  const leftParts = left.split('.');
  const rightParts = right.split('.');
  for (let index = 0; index < 3; index += 1) {
    const leftPart = leftParts[index] ?? '';
    const rightPart = rightParts[index] ?? '';
    if (leftPart.length !== rightPart.length) {
      return leftPart.length < rightPart.length ? -1 : 1;
    }
    if (leftPart !== rightPart) {
      return leftPart < rightPart ? -1 : 1;
    }
  }
  return 0;
}

function formatDate(value: string) {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—';
}

function statusTag(approval: ComputerProductionUpgradeApproval) {
  const status = {
    pending: ['gold', '待审批'],
    approved: ['blue', '发布中'],
    rejected: ['red', '已拒绝'],
    withdrawn: ['default', '已撤回'],
    expired: ['default', '已过期'],
    stale: ['orange', '基线失效'],
    published: ['green', '已发布'],
  }[approval.status] as [string, string];
  return <Tag color={status[0]}>{status[1]}</Tag>;
}

function userLabel(userID: string) {
  const identity =
    computerProductionUpgradePolicyStore.snapshot?.userIdentities.find(
      (item) => item.userUid === userID,
    );
  if (!identity) {
    return userID || '—';
  }
  return identity.name || identity.email || userID;
}

function ComputerProductionUpgradePolicyPageContent() {
  const store = computerProductionUpgradePolicyStore;
  const snapshot = store.snapshot;
  const canWrite = authStore.can(ADMIN_PERMISSIONS.desktopUpdatesWrite);
  const currentUserID = authStore.user?.user_id || '';
  const [minimumVersion, setMinimumVersion] = useState('');
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] =
    useState<ComputerProductionUpgradeApproval | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    void store.fetch().catch(() => undefined);
  }, [store]);

  useEffect(() => {
    if (snapshot?.policy.minimumVersion) {
      setMinimumVersion(snapshot.policy.minimumVersion);
    }
  }, [snapshot?.policy.minimumVersion]);

  const publicationActive = useMemo(
    () =>
      snapshot?.approvals.some(
        (approval) =>
          approval.status === 'approved' ||
          approval.publication?.status === 'pending' ||
          approval.publication?.status === 'deploying' ||
          approval.publication?.status === 'failed',
      ) || false,
    [snapshot?.approvals],
  );

  useEffect(() => {
    if (!publicationActive) {
      return;
    }
    const timer = window.setInterval(() => {
      if (!store.loading) {
        void store.fetch({ preserveHistory: true }).catch(() => undefined);
      }
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [publicationActive, store]);

  const draftComparison =
    snapshot && COMPUTER_PRODUCTION_VERSION.test(minimumVersion)
      ? compareVersions(minimumVersion, snapshot.policy.minimumVersion)
      : 0;
  const activeApproval = snapshot?.approvals.find(
    (approval) =>
      approval.status === 'pending' || approval.status === 'approved',
  );

  const submit = async () => {
    const normalizedVersion = minimumVersion.trim();
    const normalizedReason = reason.trim();
    if (
      !COMPUTER_PRODUCTION_VERSION.test(normalizedVersion) ||
      compareVersions(normalizedVersion, '0.2.0') < 0
    ) {
      message.error(
        'Computer Production 最低版本必须是至少 0.2.0 的规范 x.y.z',
      );
      return;
    }
    if (!normalizedReason) {
      message.error('必须填写变更原因');
      return;
    }
    if (
      snapshot &&
      compareVersions(normalizedVersion, snapshot.productionRelease.version) > 0
    ) {
      message.error('最低版本不能高于当前已验签 Computer Production 版本');
      return;
    }
    try {
      const result = await store.change({
        minimumVersion: normalizedVersion,
        reason: normalizedReason,
      });
      message.success(
        result.approval?.status === 'published'
          ? `已发布 AppConfig v${result.approval.finalAppConfigVersion}`
          : '已提交非本人审批',
      );
      setReason('');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '提交失败');
    }
  };

  const runReview = async (
    operation: () => Promise<unknown>,
    success: string,
  ) => {
    try {
      await operation();
      message.success(success);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '操作失败');
    }
  };

  return (
    <div className="space-y-6">
      <header className="rounded-lg border border-border bg-background p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-semibold">
            Computer Production 强制升级
          </h1>
          <Tag>
            AppConfig v{snapshot?.currentVersion?.version_number || '—'}
          </Tag>
          {activeApproval ? <Tag color="gold">存在进行中的变更</Tag> : null}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          全局 Computer Production 最低版本策略，按 Computer Release 顶层
          computerVersion
          判断。提高最低版本必须由非提交人审批；降低版本记录原因后直接发布。
        </p>
      </header>

      {store.error ? (
        <Alert type="error" showIcon message={store.error} />
      ) : null}

      <section className="rounded-lg border border-border bg-background p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold">
          线上策略与已验签 Stable
        </h2>
        <Descriptions bordered size="small" column={{ xs: 1, lg: 2 }}>
          <Descriptions.Item label="线上最低版本">
            <Typography.Text strong>
              {snapshot?.policy.minimumVersion || '—'}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Computer Production 版本">
            {snapshot?.productionRelease.version || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Stable 发布时间">
            {formatDate(snapshot?.productionRelease.publishedAt || '')}
          </Descriptions.Item>
          <Descriptions.Item label="Source SHA">
            <Typography.Text
              code
              copyable={Boolean(snapshot?.productionRelease.sourceSha)}
            >
              {snapshot?.productionRelease.sourceSha || '—'}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Release envelope SHA-256" span={2}>
            <Typography.Text
              code
              copyable={Boolean(snapshot?.productionRelease.envelopeSha256)}
            >
              {snapshot?.productionRelease.envelopeSha256 || '—'}
            </Typography.Text>
          </Descriptions.Item>
        </Descriptions>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-background p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold">策略草稿</h2>
          {draftComparison > 0 ? <Tag color="gold">提高 · 需要审批</Tag> : null}
          {draftComparison < 0 ? <Tag color="blue">降低 · 直接发布</Tag> : null}
          {draftComparison === 0 ? <Tag>无差异</Tag> : null}
        </div>
        <div className="grid gap-4 lg:grid-cols-[260px_1fr_auto] lg:items-end">
          <label
            className="space-y-1 text-sm"
            htmlFor="host-artifact-minimum-version"
          >
            <span>Computer Production 最低版本</span>
            <Input
              id="host-artifact-minimum-version"
              value={minimumVersion}
              disabled={!canWrite || Boolean(activeApproval)}
              placeholder="例如 1.4.0"
              onChange={(event) => setMinimumVersion(event.target.value)}
            />
          </label>
          <label
            className="space-y-1 text-sm"
            htmlFor="host-artifact-policy-reason"
          >
            <span>变更原因</span>
            <Input.TextArea
              id="host-artifact-policy-reason"
              value={reason}
              disabled={!canWrite || Boolean(activeApproval)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              placeholder="必填：说明破坏性变更、影响范围与回滚预期"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <Button
            loading={store.saving}
            disabled={
              !canWrite || Boolean(activeApproval) || draftComparison === 0
            }
            onClick={() => void submit()}
          >
            提交变更
          </Button>
        </div>
        {activeApproval ? (
          <Alert
            showIcon
            type="warning"
            message={`审批单 #${activeApproval.id} 正在${activeApproval.status === 'pending' ? '等待审批' : '发布'}，完成前不可创建新草稿。`}
          />
        ) : null}
      </section>

      <section className="rounded-lg border border-border bg-background p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">审批与发布记录</h2>
          <Button
            variant="outline"
            loading={store.loading}
            onClick={() => void store.fetch()}
          >
            刷新
          </Button>
        </div>
        <Table<ComputerProductionUpgradeApproval>
          rowKey="id"
          pagination={false}
          dataSource={snapshot?.approvals || []}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              width: 80,
              render: (value) => `#${value}`,
            },
            {
              title: '变更',
              render: (_, approval) => (
                <Space direction="vertical" size={0}>
                  <span>
                    {approval.baselineMinimumVersion} →{' '}
                    <strong>{approval.targetMinimumVersion}</strong>
                  </span>
                  <Typography.Text type="secondary">
                    {approval.changeKind === 'approval'
                      ? '提高版本'
                      : '降低版本'}
                  </Typography.Text>
                </Space>
              ),
            },
            { title: '状态', render: (_, approval) => statusTag(approval) },
            {
              title: '提交人 / 时间',
              render: (_, approval) => (
                <Space direction="vertical" size={0}>
                  <span>{userLabel(approval.submitterUid)}</span>
                  <Typography.Text type="secondary">
                    {formatDate(approval.createdAt)}
                  </Typography.Text>
                </Space>
              ),
            },
            { title: '原因', dataIndex: 'reason' },
            {
              title: '发布',
              render: (_, approval) =>
                approval.publication ? (
                  <Space direction="vertical" size={0}>
                    <span>{approval.publication.status}</span>
                    {approval.publication.lastError ? (
                      <Typography.Text type="danger">
                        {approval.publication.lastError}
                      </Typography.Text>
                    ) : null}
                  </Space>
                ) : (
                  '—'
                ),
            },
            {
              title: '操作',
              width: 200,
              render: (_, approval) => {
                if (approval.status !== 'pending') {
                  return '—';
                }
                if (approval.submitterUid === currentUserID) {
                  return (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canWrite || store.saving}
                      onClick={() =>
                        void runReview(
                          () => store.withdraw(approval.id),
                          '审批单已撤回',
                        )
                      }
                    >
                      撤回
                    </Button>
                  );
                }
                return (
                  <Space>
                    <Button
                      size="sm"
                      disabled={
                        !canWrite ||
                        !snapshot?.currentUserCanApprove ||
                        store.saving
                      }
                      onClick={() =>
                        void runReview(
                          () => store.approve(approval.id),
                          '已批准并开始发布',
                        )
                      }
                    >
                      批准
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={
                        !canWrite ||
                        !snapshot?.currentUserCanApprove ||
                        store.saving
                      }
                      onClick={() => {
                        setRejecting(approval);
                        setRejectionReason('');
                      }}
                    >
                      拒绝
                    </Button>
                  </Space>
                );
              },
            },
          ]}
        />
        {snapshot?.nextApprovalPageToken ? (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              loading={store.loading}
              onClick={() => void store.fetchMoreApprovals()}
            >
              加载更多
            </Button>
          </div>
        ) : null}
      </section>

      <Modal
        title={`拒绝审批单 #${rejecting?.id || ''}`}
        open={Boolean(rejecting)}
        okText="确认拒绝"
        cancelText="取消"
        okButtonProps={{ danger: true, disabled: !rejectionReason.trim() }}
        confirmLoading={store.saving}
        onCancel={() => setRejecting(null)}
        onOk={() => {
          if (!rejecting || !rejectionReason.trim()) {
            return;
          }
          void runReview(
            () => store.reject(rejecting.id, rejectionReason.trim()),
            '审批单已拒绝',
          ).then(() => setRejecting(null));
        }}
      >
        <Input.TextArea
          rows={3}
          value={rejectionReason}
          placeholder="必填：说明拒绝原因"
          onChange={(event) => setRejectionReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}

export const ComputerProductionUpgradePolicyPage = observer(
  ComputerProductionUpgradePolicyPageContent,
);
